import { FolderOpen, ChevronDown, ArrowDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { SavedProjectCard } from "./SavedProjectCard";
import type { SavedProject } from "./types";

const SORT_OPTIONS = [
  { value: "updated_at desc", label: "Newest First" },
  { value: "updated_at asc", label: "Oldest First" },
  { value: "name asc", label: "Name (A-Z)" },
  { value: "name desc", label: "Name (Z-A)" }
];

type LoadingState = "loading" | "success";

export function SavedProjectsContent() {
  const [projects, setProjects] = useState<SavedProject[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [sort, setSort] = useState<string>("updated_at desc");
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls
    if (fetchInProgressRef.current) {
      return;
    }

    const fetchProjects = async () => {
      fetchInProgressRef.current = true;
      setLoadingState("loading");

      try {
        const response = await api.designLab.getDesignProjectsList({ sort_by: sort });
        const camelCaseData = objNamesToCamelCase(response);
        setProjects(camelCaseData.projects || []);
        setLoadingState("success");
      } catch {
        // Failed to fetch projects, show empty state
        setLoadingState("success");
      } finally {
        fetchInProgressRef.current = false;
      }
    };

    fetchProjects();
  }, [sort]);

  const handleEditClick = useCallback((project: SavedProject) => {
    window.location.href = project.editDesignProjectUrl;
  }, []);

  const handleDelete = useCallback(
    async (projectId: string) => {
      setDeletingIds(prev => new Set(prev).add(projectId));

      try {
        await api.designLab.deleteDesignProject({ uuid: projectId });
        setProjects(prev => prev.filter(p => p.uuid !== projectId));
      } catch (e: any) {
        // If project not found, treat as already deleted - remove from list silently
        if (e?.response?.error === "project_not_found") {
          setProjects(prev => prev.filter(p => p.uuid !== projectId));
        } else {
          const projectName = projects.find(p => p.uuid === projectId)?.name || "project";
          // eslint-disable-next-line no-alert
          alert(`Failed to delete "${projectName}". Please try again.`);
        }
      } finally {
        setDeletingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      }
    },
    [projects]
  );

  const renderLoadingSkeleton = () => (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex-shrink-0">
        <div className="flex items-center gap-2 lg:gap-4">
          <div className="h-7 flex-1 animate-pulse rounded bg-gray-200 lg:w-40 lg:flex-none" />
          <div className="h-11 flex-1 animate-pulse rounded-md bg-gray-200 lg:w-[220px] lg:flex-none" />
        </div>
      </div>
      <div
        className="grid grid-cols-2 gap-2 lg:grid-cols-[repeat(4,240px)] lg:gap-4"
        style={{
          maxWidth: "calc(240px * 4 + 1rem * 3)"
        }}
      >
        {[...Array(4)].map((_, index) => (
          <div key={`skeleton-${index}`} className="flex w-full flex-col gap-3">
            <div className="aspect-square w-full animate-pulse rounded-lg bg-gray-200" />
            <div className="space-y-2">
              <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
            </div>
            <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center gap-6 p-8 py-16">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-gray-100">
          <FolderOpen className="size-8 text-gray-400" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">No Saved Projects</h3>
          <p className="max-w-sm text-sm text-gray-600">
            You haven&apos;t saved any design projects yet. Start creating designs and save them for
            later!
          </p>
        </div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="font-inter flex flex-col gap-4 p-4">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="flex-1 text-lg font-semibold lg:flex-none">Saved Projects</h2>
          <div className="relative flex-1 text-base lg:w-[220px] lg:flex-none">
            <ArrowDown className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-700" />
            <select
              value={sort}
              onChange={e => {
                const newSort = e.target.value;
                setSort(newSort);
              }}
              id="saved-design-projects-sort-select"
              data-testid="saved-projects-sort-select"
              className="bg-background h-11 w-full appearance-none rounded-md border border-gray-400 px-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 lg:text-base"
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value} data-testid="sort-option">
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-gray-700" />
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-2 gap-2 lg:grid-cols-[repeat(4,240px)] lg:gap-4"
        style={{
          maxWidth: "calc(240px * 4 + 1rem * 3)"
        }}
      >
        {projects.map(project => (
          <SavedProjectCard
            key={project.uuid}
            project={project}
            onEditClick={handleEditClick}
            onDelete={handleDelete}
            isDeleting={deletingIds.has(project.uuid)}
          />
        ))}
      </div>
    </div>
  );

  if (loadingState === "loading") {
    return renderLoadingSkeleton();
  }

  if (projects.length === 0) {
    return renderEmptyState();
  }

  return renderProjects();
}
